<script setup lang="ts">
import { computed, ref } from 'vue'
import ImageOptionPickerModal from '@/components/ImageOptionPickerModal.vue'

export type ImageOptionPickerItem = {
  index: number
  label: string
  name: string
  noneImagePath: string
  moveImagePath: string
}

const props = defineProps<{
  modelValue: number
  title?: string
  placeholder?: string
  items: ImageOptionPickerItem[]
  graphicBaseFolderPath?: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const pickerOpen = ref(false)

const selectedLabel = computed(() => {
  const hit = props.items.find((item) => item.index === props.modelValue)
  if (hit) return hit.label
  if (Number.isFinite(props.modelValue)) return `当前值：${props.modelValue}`
  return props.placeholder ?? '请选择图片选项'
})

function openPicker() {
  if (props.disabled) return
  pickerOpen.value = true
}
</script>

<template>
  <div
    class="image-option-picker-field"
    :class="{
      'image-option-picker-field--disabled': disabled,
    }"
  >
    <input
      class="image-option-picker-input"
      type="text"
      :value="selectedLabel"
      :placeholder="placeholder ?? '请选择图片选项'"
      :disabled="disabled"
      readonly
    />
    <button
      type="button"
      class="image-option-picker-trigger"
      :disabled="disabled"
      :title="title ?? '选择图片选项'"
      @click="openPicker"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M11.6 1.4a1.5 1.5 0 0 1 2.1 2.1l-7.3 7.3-3 0.8 0.8-3 7.4-7.2Zm1.4 2.1a.5.5 0 0 0-.7-.7L5.2 9.9l-0.4 1.4 1.4-0.4L13 3.5ZM2 13h12v1H2z"
          fill="currentColor"
        />
      </svg>
    </button>

    <ImageOptionPickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      :title="title ?? '选择图片选项'"
      :selected-index="modelValue"
      :items="items"
      :graphic-base-folder-path="graphicBaseFolderPath"
      @select="emit('update:modelValue', $event)"
    />
  </div>
</template>

<style scoped>
.image-option-picker-field {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.image-option-picker-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 4px 34px 4px 8px;
  font-size: 12px;
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  line-height: 20px;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-option-picker-trigger {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 26px;
  height: calc(100% - 2px);
  padding: 0;
  border: 0;
  border-left: 1px solid var(--theme-border);
  background: var(--theme-bg-surface);
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.image-option-picker-trigger:hover:not(:disabled) {
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
}

.image-option-picker-trigger:disabled,
.image-option-picker-input:disabled {
  cursor: not-allowed;
}

.image-option-picker-trigger svg {
  width: 14px;
  height: 14px;
}

.image-option-picker-field--disabled .image-option-picker-input {
  opacity: 0.72;
}
</style>
